import React, { memo, useCallback } from 'react';
import Flex from '@creditflowresearch/uilib/Flex';
import { TileHeader } from '@creditflowresearch/uilib/Tile';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import IconQuestion from '@creditflowresearch/uilib/Icon/IconQuestion';
import ToggleButtonGroup from '@creditflowresearch/uilib/ToggleButtonGroup';
import { Periods } from '@/creditflowresearch/ui/common/constants';

import styles from './ChartHeader.module.scss';

interface IProps {
  showVolume: boolean,
  showStackedData: boolean,
  disabledVolumeButton: boolean,
  period: string,
  showChartDataByDealSize: () => void,
  showChartDataByDealCount: () => void,
  setRenderByMonth: () => void,
  setRenderByQuarter: () => void,
  setRenderByYear: () => void,
  setShowStackedData: () => void,
  setShowGroupedData: () => void,
}

const VOLUME = 'volume';
const COUNT = 'count';
const STACKED = 'stacked';
const GROUPED = 'grouped';

const ChartTitle = ({ showVolume }: { showVolume: boolean }): React.ReactElement => (
  <div>
    <h3 className={ styles.chartTitle }>New Issue { showVolume ? 'Volume' : 'Deal Count' }</h3>
    <Tooltip wrpClassName={ styles.question } content="This chart displays new issue volume or deal count. The totals exclude bonds that are retained by the issuer or sponsor.">
      <IconQuestion className={ styles.questionFill } />
    </Tooltip>
  </div>
);

const ChartHeader = (props: IProps): React.ReactElement => {
  const handleVolumeChange = useCallback((value: string): void => {
    if (value === VOLUME && !props.showVolume) props.showChartDataByDealSize();
    if (value === COUNT && props.showVolume) props.showChartDataByDealCount();
  }, [props.showVolume, props.showChartDataByDealSize, props.showChartDataByDealCount]);

  const handlePeriodChange = useCallback((value: string): void => {
    if (value === props.period) return;
    if (value === Periods.MONTH) props.setRenderByMonth();
    if (value === Periods.QUARTER) props.setRenderByQuarter();
    if (value === Periods.YEAR) props.setRenderByYear();
  }, [props.period, props.setRenderByMonth, props.setRenderByQuarter, props.setRenderByYear]);

  const handleGroupingChange = useCallback((value: string): void => {
    if (value === STACKED && !props.showStackedData) props.setShowStackedData();
    if (value === GROUPED && props.showStackedData) props.setShowGroupedData();
  }, [props.showStackedData, props.setShowStackedData, props.setShowGroupedData]);

  return (
    <TileHeader
      title={ <ChartTitle showVolume={ props.showVolume } /> }
      dataTest="marketChartHeader"
      wrap="wrap"
    >
      <Flex gap={ 10 } direction={ { sm: 'column', xl: 'row' } }>
        <ToggleButtonGroup
          value={ [props.showVolume ? VOLUME : COUNT] }
          onChange={ handleVolumeChange }
          size="S"
          dataTest="marketChartVolumeGroup"
          fit={ { sm: 'full', md: 'inline', xl: 'inline' } }
        >
          <ToggleButtonGroup.Item
            value={ VOLUME }
            disabled={ props.disabledVolumeButton }
            dataTest="marketChartButton"
          >
            Volume
          </ToggleButtonGroup.Item>
          <ToggleButtonGroup.Item
            value={ COUNT }
            dataTest="marketChartButton"
          >
            Count
          </ToggleButtonGroup.Item>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={ [props.period] }
          onChange={ handlePeriodChange }
          size="S"
          dataTest="marketChartPeriodGroup"
          fit={ { sm: 'full', md: 'inline', xl: 'inline' } }
        >
          <ToggleButtonGroup.Item
            value={ Periods.MONTH }
            dataTest="marketChartButton"
          >
            Month
          </ToggleButtonGroup.Item>
          <ToggleButtonGroup.Item
            value={ Periods.QUARTER }
            dataTest="marketChartButton"
          >
            Quarter
          </ToggleButtonGroup.Item>
          <ToggleButtonGroup.Item
            value={ Periods.YEAR }
            dataTest="marketChartButton"
          >
            Year
          </ToggleButtonGroup.Item>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          value={ [props.showStackedData ? STACKED : GROUPED] }
          onChange={ handleGroupingChange }
          size="S"
          dataTest="marketChartGroupingGroup"
          fit={ { sm: 'full', md: 'inline', xl: 'inline' } }
        >
          <ToggleButtonGroup.Item
            value={ STACKED }
            dataTest="marketChartButton"
          >
            Stacked
          </ToggleButtonGroup.Item>
          <ToggleButtonGroup.Item
            value={ GROUPED }
            dataTest="marketChartButton"
          >
            Grouped
          </ToggleButtonGroup.Item>
        </ToggleButtonGroup>
      </Flex>
    </TileHeader>
  );
};

export default memo(ChartHeader);
